/**
 * Copy trading. Money set aside to follow someone is a sub-account of its own inside your brokerage
 * account: its own cash, its own positions, its own averages — never mixed with what you hold of the
 * same stock. On the portfolio it is one row among your holdings; pressed, it opens underneath; it has
 * a page of its own. You start it yourself, with your own limits, and you can stop it at any time.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { DEFAULT_COPY_SETTINGS, type CopyDetail, type CopyOverview, type CopySettings, type CopyView } from '@shared/copy'
import { copyApi, subscribeUserEvents } from './api'
import { Btn, Dialog, Field, Status, Switch, askName, askSure, cx, inputCls, pct, toneOf, usd } from './kit'
import { Page } from './Page'
import { useDemo } from './store'

const label = 'm-0 text-[12px] font-normal leading-4 tracking-normal text-ws-label'
const num = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 6 })
const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
const when = (ms: number) => new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
const gainText = (n: number, p: number) => `${n >= 0 ? '+' : '−'}${usd(Math.abs(n))} (${pct(p)})`
const STATE: Record<CopyView['status'], { tone: 'free' | 'off' | 'warn'; text: string }> = { active: { tone: 'free', text: 'Copying' }, paused: { tone: 'off', text: 'Paused' }, stopping: { tone: 'warn', text: 'Stopping' }, stopped: { tone: 'off', text: 'Stopped' } }

/** A person (not an agent): an initial in a circle. */
export const Person = ({ name, size = 24 }: { name: string; size?: number }) => (
  <span className="flex shrink-0 items-center justify-center rounded-full bg-ws-raise font-medium leading-none text-ws-fg-2" style={{ width: size, height: size, fontSize: size * 0.46 }} aria-hidden>
    {name.slice(0, 1).toLocaleUpperCase('tr')}
  </span>
)

/** What the server knows about copying, kept fresh: read on mount, and again whenever it says something changed. */
export function useCopies() {
  const [data, setData] = useState<CopyOverview | null>(null)
  const load = useCallback(() => void copyApi.overview().then(setData, () => setData({ live: false, allow: false, leaders: [], copies: [], followers: [] })), [])
  useEffect(() => {
    load()
    return subscribeUserEvents((e) => (e as { type: string }).type === 'copy' && load())
  }, [load])
  return { data, load }
}

const act = (job: Promise<unknown>, done: () => void, ok?: string) => {
  const s = useDemo.getState()
  void job.then(
    () => (ok && s.say(ok), done()),
    (e) => s.say(e instanceof Error ? e.message : 'That did not work'),
  )
}
const amountOf = (v: string) => Number(v.replace(/[^\d.]/g, ''))

/* ── what you can do with a copy ─────────────────────────────────────── */

export const addMoney = (c: CopyView, done: () => void) => askName({ title: `Add money to the copy of ${c.leader.name}`, label: 'Amount in dollars', value: '', cta: 'Add', done: (v) => act(copyApi.deposit(c.id, amountOf(v)), done, 'Added — it is used for the next trades') })
export const takeOut = (c: CopyView, done: () => void) => askName({ title: `Take money out (${usd(c.cash)} is free)`, label: 'Amount in dollars', value: '', cta: 'Take out', done: (v) => act(copyApi.withdraw(c.id, amountOf(v)), done, 'Taken out — it is yours to use again') })
const stopCopying = (c: CopyView, done: () => void) =>
  askSure({
    title: `Stop copying ${c.leader.name}?`,
    body: c.positions.length ? (c.live ? 'Its shares are sold at the market and the money is free again. To keep the shares as your own instead, change "When it stops" in the copy’s settings first.' : 'The server is in rehearsal: nothing is sold, the shares stay in your account as your own.') : 'It holds no shares; its cash is simply free again.',
    cta: c.settings.onStop === 'keep' ? 'Stop and keep the shares' : 'Stop and sell',
    done: () => act(copyApi.stop(c.id, c.settings.onStop), done, 'The copy is stopping'),
  })
export const sellOne = (c: CopyView, symbol: string, done: () => void) =>
  askSure({
    title: `Sell ${symbol} out of this copy?`,
    body: `Only the copy’s own shares are sold, at the market; the money stays in the copy. It is not bought back unless ${c.leader.name} buys it again.`,
    cta: `Sell ${symbol}`,
    done: () => act(copyApi.sell(c.id, symbol, 1), done, 'The sale was sent'),
  })

/* ── on the portfolio: one row per copy, opening in place ────────────── */

export function CopyRows() {
  const { data, load } = useCopies()
  const openCopy = useDemo((s) => s.openCopy)
  const [open, setOpen] = useState<string | null>(null)
  const list = (data?.copies ?? []).filter((c) => c.status !== 'stopped')
  if (!list.length) return null
  return (
    <section>
      <h2 className={label}>
        Copies <span className="text-ws-fg-4">· money set aside to follow someone, each a sub-account of its own</span>
      </h2>
      <div className="mt-2 divide-y divide-ws-hair border-y border-ws-line">
        {list.map((c) => (
          <div key={c.id}>
            <button type="button" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)} className="flex min-h-12 w-full items-center gap-3 py-2 text-left text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover">
              <ChevronRight size={13} strokeWidth={2} className={cx('shrink-0 text-ws-fg-4 transition-transform duration-150', open === c.id && 'rotate-90')} />
              <Person name={c.leader.name} />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-ws-fg">{c.leader.name}</span>
                <span className="ml-2 text-ws-fg-3">
                  {c.positions.length} {c.positions.length === 1 ? 'stock' : 'stocks'} · since {day(c.since)}
                </span>
              </span>
              <Status tone={STATE[c.status].tone}>{c.live ? STATE[c.status].text : 'Rehearsal'}</Status>
              <span className="w-[104px] shrink-0 text-right tabular-nums text-ws-fg">{usd(c.value)}</span>
              <span className={cx('hidden w-[150px] shrink-0 text-right tabular-nums sm:block', toneOf(c.gain))}>{gainText(c.gain, c.gainPct)}</span>
            </button>
            {open === c.id && (
              <div className="pb-4 pl-[25px]">
                <CopyPositions c={c} onChanged={load} />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Btn variant="outline" onClick={() => addMoney(c, load)}>
                    Add money
                  </Btn>
                  <Btn variant="outline" disabled={c.cash < 0.01} onClick={() => takeOut(c, load)}>
                    Take out
                  </Btn>
                  <Btn variant="outline" onClick={() => openCopy(c.id)}>
                    Open
                  </Btn>
                  <span className="ml-auto text-[12px] tracking-normal text-ws-fg-3">
                    {usd(c.putIn)} put in · {usd(c.cash)} cash
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

function CopyPositions({ c, onChanged }: { c: CopyView; onChanged: () => void }) {
  if (!c.positions.length) return <p className="m-0 py-3 text-[12px] tracking-normal text-ws-fg-3">Nothing has been copied yet. The next trade {c.leader.name} makes is the first.</p>
  return (
    <div className="divide-y divide-ws-hair">
      {c.positions.map((p) => (
        <div key={p.symbol} className="flex min-h-10 items-center gap-3 py-[6px] text-[13px] tracking-normal">
          <span className="w-[64px] shrink-0 font-medium text-ws-fg">{p.symbol}</span>
          <span className="min-w-0 flex-1 truncate text-ws-fg-3">
            {num(p.qty)} at {usd(p.avg)}
            {p.stale ? ' · no live price' : ''}
          </span>
          <span className="w-[96px] shrink-0 text-right tabular-nums text-ws-fg">{usd(p.value)}</span>
          <span className={cx('hidden w-[150px] shrink-0 text-right tabular-nums sm:block', toneOf(p.gain))}>{gainText(p.gain, p.gainPct)}</span>
          <button type="button" disabled={c.status === 'stopping'} onClick={() => sellOne(c, p.symbol, onChanged)} className="shrink-0 rounded-[6px] px-2 py-1 text-[12px] text-ws-fg-3 transition-colors hover:bg-ws-hover hover:text-ws-down disabled:opacity-40">
            Sell
          </button>
        </div>
      ))}
    </div>
  )
}

/* ── a copy's own page ───────────────────────────────────────────────── */

const Fig = ({ k, children, sub }: { k: string; children: ReactNode; sub?: ReactNode }) => (
  <div className="min-w-0 py-4 sm:px-5 sm:first:pl-0">
    <div className="text-[12px] tracking-normal text-ws-fg-3">{k}</div>
    <div className="mt-1 text-[20px] font-medium leading-7 tracking-[-0.01em] text-ws-fg tabular-nums">{children}</div>
    {sub && <div className="mt-1 text-[12px] tracking-normal text-ws-fg-3">{sub}</div>}
  </div>
)

/** The limits of a copy: the follower's own, changeable while it runs. */
function Limits({ value, onChange }: { value: CopySettings; onChange: (s: CopySettings) => void }) {
  const row = 'flex min-h-11 items-center gap-3 px-3 py-[6px]'
  const box = 'flex h-8 w-[84px] shrink-0 items-center rounded-[8px] border border-ws-line bg-ws-field px-[10px] text-[13px] tracking-normal focus-within:border-ws-focus'
  const Num = ({ v, min, max, on, aria }: { v: number; min: number; max: number; on: (n: number) => void; aria: string }) => (
    <label className={box}>
      <input defaultValue={v} key={v} onBlur={(e) => on(Math.max(min, Math.min(max, Number(e.target.value) || v)))} inputMode="decimal" aria-label={aria} className="h-full min-w-0 flex-1 text-right tabular-nums" />
      <span className="pl-1 text-ws-fg-4">%</span>
    </label>
  )
  return (
    <div className="divide-y divide-ws-hair rounded-[8px] border border-ws-line">
      <div className={row}>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] leading-[18px] tracking-normal text-ws-fg">Never chase a price</span>
          <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">A buy is skipped when your price is this far above what they paid</span>
        </span>
        <Num v={value.slippagePct} min={0.1} max={5} aria="Slippage limit" on={(n) => onChange({ ...value, slippagePct: n })} />
      </div>
      <div className={row}>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] leading-[18px] tracking-normal text-ws-fg">Largest single stock</span>
          <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">No stock grows past this share of the copy</span>
        </span>
        <Num v={value.maxPositionPct} min={5} max={100} aria="Largest single stock" on={(n) => onChange({ ...value, maxPositionPct: n })} />
      </div>
      <div className={row}>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] leading-[18px] tracking-normal text-ws-fg">Stop by itself after a loss</span>
          <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">{value.stopAtPct == null ? 'Off: it runs until you stop it' : 'When the copy is down this much of the money put in'}</span>
        </span>
        {value.stopAtPct != null && <Num v={value.stopAtPct} min={1} max={95} aria="Stop after a loss of" on={(n) => onChange({ ...value, stopAtPct: n })} />}
        <Switch on={value.stopAtPct != null} onChange={(v) => onChange({ ...value, stopAtPct: v ? 40 : null })} label="Stop by itself after a loss" />
      </div>
      <div className={row}>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] leading-[18px] tracking-normal text-ws-fg">When it stops</span>
          <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">{value.onStop === 'sell' ? 'Its shares are sold at the market' : 'Its shares stay in your account as your own'}</span>
        </span>
        <div className="flex h-8 shrink-0 gap-px rounded-[8px] border border-ws-line bg-ws-field p-[3px] text-[12px]">
          {(['sell', 'keep'] as const).map((m) => (
            <button key={m} type="button" onClick={() => onChange({ ...value, onStop: m })} className={cx('rounded-[5px] px-3 transition-colors duration-150', value.onStop === m ? 'bg-ws-raise text-ws-fg' : 'text-ws-fg-3 hover:text-ws-fg')}>
              {m === 'sell' ? 'Sell' : 'Keep'}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export function CopyPage({ id }: { id: string }) {
  const [d, setD] = useState<CopyDetail | null>(null)
  const [gone, setGone] = useState(false)
  const load = useCallback(() => void copyApi.detail(id).then(setD, () => setGone(true)), [id])
  useEffect(() => {
    load()
    return subscribeUserEvents((e) => (e as { type: string }).type === 'copy' && load())
  }, [load])
  if (gone) return <div className="p-10 text-[12px] tracking-normal text-ws-fg-4">That copy is not there.</div>
  if (!d) return <div className="p-10 text-[12px] tracking-normal text-ws-fg-4">Loading…</div>
  const over = d.status === 'stopped'
  return (
    <Page>
      <div className="mx-auto max-w-[920px] space-y-8">
        <section>
          <div className="flex items-center gap-3">
            <Person name={d.leader.name} size={32} />
            <div className="min-w-0 flex-1">
              <h1 className="m-0 truncate text-[20px] font-medium leading-7 tracking-[-0.01em] text-ws-fg">Copy of {d.leader.name}</h1>
              <div className="flex flex-wrap items-center gap-x-3 text-[12px] tracking-normal text-ws-fg-3">
                <Status tone={STATE[d.status].tone}>{STATE[d.status].text}</Status>
                <span>since {day(d.since)}</span>
                <span>in your {d.broker === 'ibkr' ? 'Interactive Brokers' : 'Robinhood'} account</span>
                {d.stopReason && <span>{d.stopReason}</span>}
              </div>
            </div>
            {!over && (
              <div className="flex shrink-0 flex-wrap justify-end gap-2">
                <Btn variant="outline" onClick={() => addMoney(d, load)}>
                  Add money
                </Btn>
                <Btn variant="outline" disabled={d.cash < 0.01} onClick={() => takeOut(d, load)}>
                  Take out
                </Btn>
                {d.status !== 'stopping' && (
                  <Btn variant="outline" onClick={() => act(d.status === 'paused' ? copyApi.resume(d.id) : copyApi.pause(d.id), load)}>
                    {d.status === 'paused' ? 'Resume' : 'Pause'}
                  </Btn>
                )}
                <Btn variant="danger" disabled={d.status === 'stopping'} onClick={() => stopCopying(d, load)}>
                  Stop
                </Btn>
              </div>
            )}
          </div>
          {!d.live && (
            <p className="m-0 mt-4 rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
              <span className="text-ws-warn">Rehearsal.</span> This server works every copied trade out and writes down what it would have sent — and sends nothing. The lines below marked “would have sent” are those. It goes live when the person who runs this server switches it on.
            </p>
          )}
          <div className="mt-4 grid grid-cols-2 divide-ws-line border-y border-ws-line sm:grid-cols-4 sm:divide-x">
            <Fig k="Worth now" sub={<span className={toneOf(d.day)}>{d.day >= 0 ? '▲' : '▼'} {usd(Math.abs(d.day))} today</span>}>
              {usd(d.value)}
            </Fig>
            <Fig k="Put in" sub={d.withdrawn > 0 ? `${usd(d.deposited)} in · ${usd(d.withdrawn)} out` : undefined}>
              {usd(d.putIn)}
            </Fig>
            <Fig k="Made">
              <span className={toneOf(d.gain)}>{pct(d.gainPct)}</span>
            </Fig>
            <Fig k="Cash in the copy" sub="waits for the next trade">
              {usd(d.cash)}
            </Fig>
          </div>
        </section>

        <section>
          <h2 className={label}>What it holds</h2>
          <div className="mt-2 border-y border-ws-line">
            <CopyPositions c={d} onChanged={load} />
          </div>
        </section>

        {!over && (
          <section>
            <h2 className={cx(label, 'mb-2')}>Your limits</h2>
            <Limits value={d.settings} onChange={(s) => act(copyApi.settings(d.id, s), load)} />
            <p className="m-0 mt-2 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
              A buy is sized by the share of their account {d.leader.name} used, applied to this copy’s own value. A sale of theirs sells the same share of what the copy holds. {d.leader.name} is told nothing about your money.
            </p>
          </section>
        )}

        <section>
          <h2 className={label}>What happened</h2>
          <div className="mt-2 divide-y divide-ws-hair border-y border-ws-line">
            {d.log.length ? (
              d.log.slice(0, 120).map((l, i) => (
                <div key={i} className="flex min-h-10 items-baseline gap-4 py-2 text-[13px] tracking-normal">
                  <span className="w-[128px] shrink-0 text-[12px] text-ws-fg-4">{when(l.at)}</span>
                  <span className={cx('min-w-0 flex-1', l.kind === 'skipped' || l.kind === 'rehearsed' || l.kind === 'note' ? 'text-ws-fg-3' : l.kind === 'failed' ? 'text-ws-down' : 'text-ws-fg-2')}>{l.text}</span>
                </div>
              ))
            ) : (
              <p className="m-0 py-4 text-[12px] tracking-normal text-ws-fg-3">Nothing yet.</p>
            )}
          </div>
        </section>
      </div>
    </Page>
  )
}

/* ── starting one ────────────────────────────────────────────────────── */

export function StartCopy({ leader, onClose, onStarted }: { leader: { id: string; name: string } | null; onClose: () => void; onStarted: () => void }) {
  const [amount, setAmount] = useState('')
  const [settings, setSettings] = useState<CopySettings>(DEFAULT_COPY_SETTINGS)
  const [terms, setTerms] = useState<string | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  useEffect(() => {
    if (!leader) return
    setAmount('')
    setSettings(DEFAULT_COPY_SETTINGS)
    setAgreed(false)
    setProblem('')
    void copyApi.consent().then((c) => setTerms(c.text), () => setTerms(null))
  }, [leader?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const value = amountOf(amount)
  const ok = value >= 10 && agreed && !!terms
  const start = async () => {
    if (!leader || !ok) return
    setBusy(true)
    setProblem('')
    try {
      await copyApi.start({ leaderId: leader.id, amount: value, settings })
      useDemo.getState().say(`You are copying ${leader.name}`)
      onStarted()
      onClose()
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'It could not be started')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open={!!leader}
      onClose={onClose}
      title={leader ? `Copy ${leader.name}` : ''}
      width={560}
      footer={
        <>
          <Btn variant="outline" onPanel onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={!ok || busy} onClick={() => void start()}>
            {busy ? 'Starting…' : 'Start copying'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Money to set aside" hint="At least $10, from your free cash">
          <label className={cx(inputCls, 'flex items-center')}>
            <span className="text-ws-fg-4">$</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="0.00" className="h-full min-w-0 flex-1 pl-1 tabular-nums" autoFocus aria-label="Money to set aside" />
          </label>
        </Field>
        <div>
          <h3 className={cx(label, 'mb-[6px]')}>Your limits</h3>
          <Limits value={settings} onChange={setSettings} />
        </div>
        <div>
          <h3 className={cx(label, 'mb-[6px]')}>What you are agreeing to</h3>
          <div className="max-h-[180px] overflow-y-auto whitespace-pre-wrap rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-2">{terms ?? 'Loading…'}</div>
          <label className="mt-3 flex cursor-pointer items-start gap-3 text-[13px] leading-5 tracking-normal text-ws-fg-2">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-[3px] h-4 w-4 shrink-0 accent-white" />
            I have read this. Starting this copy and its limits are my own decision.
          </label>
        </div>
        {problem && <p className="m-0 text-[12px] leading-[18px] tracking-normal text-ws-down">{problem}</p>}
      </div>
    </Dialog>
  )
}
